import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import {
  FaCaretDown,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaTiktok,
  FaMediumM,
  FaGlobe,
  FaArrowLeft
} from 'react-icons/fa';
import {
  Row,
  ImageContainer,
  Image,
  NameContainer,
  Name,
  Title,
  ReadBio,
  Location,
  BioContainer,
  BioItem,
  BioTitle,
  LocationRow,
  StyledFlag,
  MediaIcons,
  BackButton,
  MobileBio
} from './styled';
import { Breadcrumbs } from '../../styled';

const MemberDetails = React.forwardRef(
  (
    {
      teamMember,
      onClose,
      onCountrySelect,
      selectedCountry,
      isVisible,
      showToggle,
      scrollToMember,
      ...props
    },
    ref
  ) => {
    const [showBio, setShowBio] = useState(false);
    const [showBird, setShowBird] = useState(false);
    const imageContainerRef = useRef();

    if (!teamMember) return null;

    const {
      photo,
      bird,
      firstName,
      middleName,
      lastName,
      role,
      bio,
      city,
      country,
      favoriteCountry,
      workingRemotely,
      whatBird,
      whyBird
    } = teamMember;
    const photoSrc = photo?.[0]?.url;
    const birdSrc = bird?.[0]?.url;
    const splitCountry = country?.split('(');
    const countryName = splitCountry?.[0].trim();
    const countryCode = splitCountry?.[1]?.substring(0, splitCountry[1].length - 1);

    useEffect(() => {
      if (!imageContainerRef.current) return;
      const handleScroll = () => {
        const el = imageContainerRef?.current?.getBoundingClientRect();

        if (showBio) {
          setShowBird(false);
        } else if (el?.top + el?.height / 2 < window.innerHeight / 2.5) {
          if (!showBird) {
            setShowBird(true);
          }
        } else if (showBird) {
          setShowBird(false);
        }
      };

      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }, [imageContainerRef, showBird, showBio]);

    const renderLocation = () => {
      let location = countryName || city;

      if (countryName && city) {
        location = `${city}, ${countryName}`;
      }

      if (countryName) {
        return (
          <Location>
            {countryCode && <StyledFlag code={countryCode} height={16} />}
            <span>{location}</span>
          </Location>
        );
      }
      return (
        <Location>
          {countryCode && <StyledFlag code={countryCode} height={16} />}
          {`${city}`}
        </Location>
      );
    };

    const renderQuestions = () => {
      return (
        <div>
          {favoriteCountry && (
            <BioItem>
              <BioTitle>Favorite country</BioTitle>
              {`"${favoriteCountry}"`}
            </BioItem>
          )}
          {workingRemotely && (
            <BioItem>
              <BioTitle>Best part about working remotely</BioTitle>
              {`"${workingRemotely}"`}
            </BioItem>
          )}
          {whatBird && whyBird && (
            <BioItem>
              <BioTitle>My bird is {whatBird.toLowerCase()} because</BioTitle>
              {`"${whyBird}"`}
            </BioItem>
          )}
        </div>
      );
    };

    const onBack = () => {
      if (selectedCountry) {
        onCountrySelect(selectedCountry);
      } else {
        onClose();
      }
    };

    const renderMediaIcons = () => {
      const { twitter, instagram, youtube, tiktok, medium, website } = teamMember || {};

      const openLink = (url) => window.open(url, '_blank');

      return (
        <MediaIcons>
          {twitter && <FaTwitter onClick={() => openLink(twitter)} />}
          {instagram && <FaInstagram onClick={() => openLink(instagram)} />}
          {youtube && <FaYoutube onClick={() => openLink(youtube)} />}
          {tiktok && <FaTiktok onClick={() => openLink(tiktok)} />}
          {medium && <FaMediumM onClick={() => openLink(medium)} />}
          {website && <FaGlobe onClick={() => openLink(website)} />}
        </MediaIcons>
      );
    };

    const toggleBio = () => {
      scrollToMember(teamMember);
      setShowBio(!showBio);
      setShowBird(showBio);
    };

    const bioVariants = {
      visible: {
        height: 'auto'
      },
      hidden: {
        height: 0
      }
    };

    return (
      <motion.div
        {...props}
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        transition={{ duration: 0.2 }}
        style={{ width: '100%' }}
        ref={ref}
      >
        <Breadcrumbs>
          <span onClick={onClose}>World</span> /{' '}
          <span
            onClick={(e) => {
              e.stopPropagation();
              onCountrySelect(countryName);
            }}
          >
            {countryName}
          </span>{' '}
          / <b>{`${firstName} ${middleName ? `${middleName} ` : ''}${lastName}`}</b>
        </Breadcrumbs>
        <BioContainer>
          <div>
            <Row>
              {photo && (
                <ImageContainer
                  ref={imageContainerRef}
                  onClick={() => showToggle && toggleBio()}
                >
                  {birdSrc && <Image isBird src={birdSrc} alt={firstName} />}
                  <Image
                    hideOnHover={birdSrc}
                    hideOnScroll={birdSrc && showBird}
                    src={photoSrc}
                    alt={firstName}
                  />
                </ImageContainer>
              )}
              <NameContainer>
                <Name>
                  <b>{firstName}</b>{' '}
                  {middleName ? `\n${middleName}\n ${lastName}` : `\n${lastName}`}
                </Name>
                <Title>{role}</Title>
              </NameContainer>
            </Row>

            <LocationRow>{renderLocation()}</LocationRow>

            {!showToggle && <BioItem>{bio}</BioItem>}
            {!showToggle && renderMediaIcons()}
          </div>
          <div>
            {!showToggle && renderQuestions()}

            <MobileBio
              initial="hidden"
              animate={showBio ? 'visible' : 'hidden'}
              variants={bioVariants}
              transition={{ duration: 0.4 }}
            >
              <BioItem>{bio}</BioItem>
              {renderQuestions()}
              {renderMediaIcons()}
            </MobileBio>
          </div>

          {showToggle && (
            <ReadBio showBio={showBio} onClick={toggleBio}>
              {showBio ? 'Close bio' : 'Read bio'}
              <FaCaretDown />
            </ReadBio>
          )}
        </BioContainer>
        <BackButton onClick={onBack}>
          <FaArrowLeft />
          Back
        </BackButton>
      </motion.div>
    );
  }
);

MemberDetails.propTypes = {
  teamMember: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    middleName: PropTypes.string,
    lastName: PropTypes.string.isRequired,
    role: PropTypes.string,
    photo: PropTypes.array,
    bird: PropTypes.array,
    bio: PropTypes.string,
    favoriteCountry: PropTypes.string,
    workingRemotely: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    email: PropTypes.string,
    whatBird: PropTypes.string,
    whyBird: PropTypes.string
  }),
  selectedCountry: PropTypes.string,
  onClose: PropTypes.func,
  isVisible: PropTypes.bool,
  onCountrySelect: PropTypes.func,
  scrollToMember: PropTypes.func,
  showToggle: PropTypes.bool
};

export default MemberDetails;
